import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
// *** NEW: Import initialized Firebase instances and useAuth hook ***
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
// ------------------------------------------------------------------

// FIX: Changed from default import to NAMED IMPORT to match the hook's export style.
import { useSmartNotifications } from '../hooks/useSmartNotifications';
// Modular imports: expecting named exports for icons
import { BellIcon, ExternalLinkIcon, CheckCircleIcon } from './Icons';

/**
 * Renders the notification bell icon and a dropdown list of recent notifications.
 * Handles navigation when a notification is clicked and marks it as read.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // *** NEW: Get userId and auth status from the successful useAuth hook ***
  const { user, validationStatus } = useAuth();
  const userId = user?.uid || null;
  const isAuthReady = validationStatus === 'success'; // Ready only when validated
  // -----------------------------------------------------------------------

  const bellRef = useRef(null);
  const popupRef = useRef(null);
  
  // DEBUG LOGGING: Log auth status and userId to ensure hook dependencies are ready.
  useEffect(() => {
    if (!db) {
      console.error('[NotificationBell] CRITICAL: DB object is NULL. Notifications cannot be loaded. Check src/firebase.js.');
    } else {
      console.log('[NotificationBell] DB object successfully imported.');
    }

    if (isAuthReady) {
      if (userId) {
        console.log(`[NotificationBell] Auth Ready via useAuth. Hook should now run with User ID: ${userId}`);
      } else {
        console.warn('[NotificationBell] Auth Ready, but User ID is null. Notifications will not load.');
      }
    } else {
      console.log('[NotificationBell] Awaiting Firebase context/ID...');
    }
    
  }, [isAuthReady, userId]);


  // Use the external hook for all data and actions, passing the dependencies.
  // We only call the hook if DB and UserId are available to prevent errors.
  const shouldRunHook = !!db && !!userId;
  
  const {
    stats,
    notifications,
    markAsRead,
  } = useSmartNotifications(db, userId); // Hook uses db/userId, its internal useEffect handles the `shouldRunHook` logic implicitly.


  // Extract unreadCount from stats object
//  const unreadCount = stats?.unreadCount || 0;
    // FIX: Calcular unreadCount diretamente das notificações
    const unreadCount = useMemo(() => {
      return notifications?.filter(n => !n.isRead).length || 0;
}, [notifications]);

 // DEBUG: Monitorar mudanças em stats
useEffect(() => {
  console.log('[NotificationBell] 🔔 stats object:', stats);
  console.log('[NotificationBell] 🔔 unreadCount:', unreadCount);
  console.log('[NotificationBell] 🔔 notifications length:', notifications?.length);
}, [stats, unreadCount, notifications]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        bellRef.current &&
        !bellRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handles the click event for an individual notification item.
   * 1. Marks the notification as read.
   * 2. Determines the appropriate route and navigates.
   */
  const handleNotificationClick = async (notification) => {
    if (!notification.id) return;

    try {
      // Check against 'isRead' field from the refactored hook
      if (!notification.isRead) {
        await markAsRead(notification.id);
      }

      let targetPath = '/notifications'; // Default fallback

      // Determine navigation path based on notification context/metadata fields
      // The server sends the exact page in `link` (/trades/<id>, /chat/<id>...).
      if (notification.type === 'CHAT' && notification.tradeId) {
        targetPath = `/chat/${notification.tradeId}`; // chat id = trade id
      } else if (typeof notification.link === 'string' && notification.link.startsWith('/')) {
        targetPath = notification.link;
      } else if (notification.tradeId || notification.metadata?.tradeId) {
        targetPath = `/trades/${notification.tradeId || notification.metadata?.tradeId}`;
      } else if (notification.chatId || notification.entityId) {
        targetPath = `/chat/${notification.chatId || notification.entityId}`;
      } else if (notification.itemId || notification.metadata?.itemId) {
        targetPath = `/market/${notification.itemId || notification.metadata?.itemId}`;
      } else {
        targetPath = '/notifications';
      }
      
      // Safety check for invalid path
      if (targetPath.includes('undefined') || targetPath.endsWith('/')) {
        targetPath = '/notifications';
      }

      navigate(targetPath);
      setIsPopupOpen(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
      navigate('/notifications');
      setIsPopupOpen(false);
    }
  };

  const NotificationItem = ({ notification }) => {
    // The hook ensures notification.createdAt is a Date object, if not, fallback to new Date()
    const dateToUse = notification.createdAt instanceof Date ? notification.createdAt : new Date();

    const timeAgo = formatDistanceToNow(dateToUse, { addSuffix: true });

    // Using the 'isRead' field provided by the refactored hook
    const isRead = notification.isRead || false;

    // --- NEW LOGIC: Display message count for coalesced chat notifications ---
    let title = notification.title || 'New Update';
    const messageCount = notification.messageCount;
    
    if (notification.type === 'CHAT' && messageCount > 1) {
        title = `${messageCount} Novas Mensagens`; // Use a clear, grouped title
    }
    // -------------------------------------------------------------------------


    return (
      <div
        // Conditional styling based on read status
        className={`flex items-start p-3 border-b border-gray-100 cursor-pointer transition-colors ${
          isRead ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100'
        }`}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className="flex-shrink-0 pt-1">
          {isRead ? (
            <CheckCircleIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <BellIcon className="w-5 h-5 text-blue-600" />
          )}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${isRead ? 'text-gray-600' : 'text-gray-800'}`}>
            {title} {/* Use the potentially updated title */}
          </p>
          <p className={`mt-1 text-xs ${isRead ? 'text-gray-500' : 'text-blue-600'}`}>{notification.body}</p>
          <p className="mt-1 text-xs text-gray-400">{timeAgo}</p>
        </div>
        
      </div>
    );
  };

  return (
    // Outer container should be relative for dropdown, but we add flex-shrink-0 for stability
    <div className="relative inline-block z-40 flex-shrink-0" ref={bellRef}>
      <button
        onClick={() => setIsPopupOpen(!isPopupOpen)}
        // FIX: Reduced padding (p-2 to p-1.5) to shrink the exaggerated gray spot
        className="relative p-1.5 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700 focus:outline-none"
        aria-label="Notifications"
      >
        <BellIcon className="w-6 h-6" />
        {/* Unread Counter Badge */}
        {unreadCount > 0 && (
          <span
            // **FIX:** Removed transform. Positioned with top-0.5 and right-0.5 to place it *inside* the button.
            // This prevents clipping from any parent element with 'overflow-hidden'.
            // Added a ring for better contrast and visibility.
            className="absolute top-0.5 right-0.5 block h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-xs font-semibold flex items-center justify-center z-50 ring-2 ring-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isPopupOpen && isAuthReady && (
        <div
          ref={popupRef}
          className="absolute right-0 mt-3 w-80 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-200"
        >
          <div className="py-2 px-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-md font-semibold text-gray-800">Notificações</h2>
            {/* Link to the full Notifications Screen */}
            <button
              onClick={() => { navigate('/trades'); setIsPopupOpen(false); }}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              Ver Trocas
              <ExternalLinkIcon className="w-4 h-4 ml-1" />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              // Map over the notifications
              notifications.map((notif) => <NotificationItem key={notif.id} notification={notif} />)
            ) : (
              <p className="p-4 text-sm text-gray-500 text-center">No recent notifications.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
